/**
 * Smooth scrolls links to #reviews to the reviews anchor block.
 *
 * Product lists between the link and the reviews load while the page scrolls
 * and shrink (a list can go from 562px to 129px), which moves the anchor during
 * and after the scroll. So:
 * - every animation frame re-reads where the anchor is, so the page moves in
 *   one motion instead of aiming at a stale position, and
 * - a ResizeObserver keeps the anchor in place while the layout keeps shifting.
 *   Its callback runs after layout and before paint, so the correction is
 *   never visible as a jump.
 */

const MIN_DURATION = 500;
const MAX_DURATION = 1000;
// Stop following the anchor once the layout has been still this long after
// the animation (the lists shrink up to ~1.2s after the scroll ends)
const SETTLE_TIME = 1500;
const MAX_FOLLOW_TIME = 5000;

/**
 * @param {number} t - Progress from 0 to 1.
 * @returns {number}
 */
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * @param {HTMLElement} anchor
 * @returns {number} The scroll position that puts the anchor at the top.
 */
function targetFor(anchor) {
  const offset = parseFloat(getComputedStyle(anchor).scrollMarginTop) || 0;
  return anchor.getBoundingClientRect().top + window.scrollY - offset;
}

/**
 * `behavior: 'instant'` overrides the theme's `scroll-behavior: smooth`, which
 * would otherwise animate every single frame.
 *
 * @param {number} top
 */
function jumpTo(top) {
  window.scrollTo({ top, behavior: /** @type {ScrollBehavior} */ ('instant') });
}

/**
 * @param {HTMLElement} anchor
 */
function scrollToAnchor(anchor) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const start = window.scrollY;
  const distance = Math.abs(targetFor(anchor) - start);
  const duration = reduceMotion ? 0 : Math.min(MAX_DURATION, Math.max(MIN_DURATION, distance / 4));
  const startTime = performance.now();
  // Settling is measured from the end of the animation
  let lastShift = startTime + duration;
  let done = false;
  let focused = false;

  // preventDefault() on the link skips the browser moving the focus start
  // point, so do it once the scroll ends: the next Tab then starts at the reviews
  const focusAnchor = () => {
    if (focused) return;
    focused = true;
    anchor.focus({ preventScroll: true });
  };

  /** @returns {number} */
  const position = () => {
    const progress = duration ? Math.min(1, (performance.now() - startTime) / duration) : 1;
    return start + (targetFor(anchor) - start) * easeInOutCubic(progress);
  };

  const observer = new ResizeObserver(() => {
    lastShift = Math.max(lastShift, performance.now());
    jumpTo(position());
  });

  const stop = () => {
    if (done) return;
    done = true;
    observer.disconnect();
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
    // No focusAnchor() here: stop() also runs when the visitor interrupts the
    // scroll, and a Tab would then continue from the reviews. step() focuses
    // the anchor once the animation ends on its own.
  };

  // The visitor scrolling themselves always wins
  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
  observer.observe(document.body);

  const step = () => {
    if (done) return;

    const now = performance.now();
    jumpTo(position());

    const animating = now - startTime < duration;
    if (!animating) focusAnchor();
    const settled = !animating && now - lastShift > SETTLE_TIME;

    if (animating || (!settled && now - startTime < MAX_FOLLOW_TIME)) {
      requestAnimationFrame(step);
    } else {
      stop();
    }
  };

  requestAnimationFrame(step);
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest('a[href="#reviews"]');
  const anchor = document.getElementById('reviews');
  if (!link || !anchor) return;

  event.preventDefault();
  history.replaceState(null, '', '#reviews');
  scrollToAnchor(anchor);
});
